/** The standalone speaking bot — an open-ended voice conversation.
 *
 * The learner picks a language, level and (optionally) a topic, presses
 * Start ONCE, and then just talks: the bot greets aloud, the mic arms
 * itself, and the two take turns continuously until the learner ends it.
 * Nothing auto-stops it mid-practice. On close it gives friendly feedback.
 *
 * The loop itself is the shared useVoiceConversation hook (same one the
 * exam speaking partner uses); this page owns only the setup screen.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/exams";
import { AskDoubt } from "../../components/AskDoubt";
import { Badge, Button, Card, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { sttAvailable } from "../../utils/stt";
import { FeedbackView, Transcript } from "./SpeakingPartner";
import { useVoiceConversation } from "./useVoiceConversation";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
/** Speech-recognition locales for the supported chat languages. */
const LOCALES: Record<string, string> = {
  de: "de-DE", en: "en-US", fr: "fr-FR", es: "es-ES", it: "it-IT",
};

export function SpeakingBotPage() {
  const t = useT();
  const [status, setStatus] = useState<api.FreeChatStatus | null>(null);
  const [language, setLanguage] = useState("de");
  // "auto" = adapt to the learner's latest read (sent as null); otherwise an
  // explicit CEFR level.
  const [level, setLevel] = useState("auto");
  const [topic, setTopic] = useState("");
  const [input, setInput] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .freeSpeakingStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) setStatus({ configured: false, quota: null, languages: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const chat = useVoiceConversation({
    startSession: async () => {
      const r = await api.startFreeSpeaking({
        language,
        level: level === "auto" ? null : level,
        topic: topic.trim() || undefined,
      });
      setStatus((s) => (s ? { ...s, quota: r.quota } : s));
      return r;
    },
    lang: LOCALES[language] ?? "de-DE",
  });
  const { session, messages, turns, busy, listening, error, ended } = chat;

  const onSendTyped = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    chat.sendText(text);
  };

  if (!status) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  const header = (
    <div>
      <h1 className="text-3xl font-extrabold">{t("bot.title")}</h1>
      <p className="mt-1 text-muted">{t("bot.subtitle")}</p>
    </div>
  );

  if (!status.configured) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <Card>
          <p role="status" className="text-sm text-muted">{t("speaking.notConfigured")}</p>
        </Card>
      </div>
    );
  }

  if (ended) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <Card>
          <FeedbackView session={ended} onAgain={chat.reset} />
        </Card>
      </div>
    );
  }

  // Setup screen — pick language, level, optional topic.
  if (!session) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <Card>
          <p className="text-5xl" aria-hidden>🗣️</p>
          <p className="mt-3 text-sm text-ink">{t("bot.intro")}</p>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="bot-language" className="text-sm font-semibold text-ink">
                {t("bot.language")}
              </label>
              <select
                id="bot-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
              >
                {status.languages.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="bot-level" className="text-sm font-semibold text-ink">
                {t("bot.level")}
              </label>
              <select
                id="bot-level"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
              >
                <option value="auto">{t("bot.levelAuto")}</option>
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              {level === "auto" && (
                <p className="mt-1 text-xs text-muted">
                  {status.suggested_level
                    ? t("bot.levelAutoAt", { level: status.suggested_level })
                    : t("bot.levelAutoHint")}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="bot-topic" className="text-sm font-semibold text-ink">
              {t("bot.topic")}
            </label>{" "}
            <span className="text-xs text-muted">{t("bot.topicHint")}</span>
            <input
              id="bot-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("bot.topicPlaceholder")}
              className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
            />
          </div>

          {!sttAvailable() && (
            <p className="mt-4 rounded-control bg-canvas p-3 text-xs text-muted">
              {t("speaking.micUnavailable")}
            </p>
          )}
          {error && <p role="alert" className="mt-3 text-sm text-danger">{error}</p>}

          <div className="mt-5 flex items-center justify-between">
            <span className="text-xs text-muted">
              {status.quota && t("speaking.quotaUsed", {
                used: status.quota.used, limit: status.quota.limit,
              })}
            </span>
            <Button onClick={() => void chat.start()} loading={busy}>
              🎤 {t("bot.start")}
            </Button>
          </div>
        </Card>
        <AskDoubt />
      </div>
    );
  }

  // Live conversation — runs until the learner ends it.
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {header}
      <Card>
        <Transcript turns={messages} />

        <div className="mt-4 flex items-center gap-3 text-sm text-muted" data-testid="bot-status">
          {busy && (
            <span className="inline-flex items-center gap-2">
              <Spinner size={14} /> {t("speaking.thinking")}
            </span>
          )}
          {!busy && listening && <span>🎙️ {t("speaking.listening")}</span>}
          <span className="ml-auto">{turns} {t("speaking.turnsWord")}</span>
        </div>

        {error && <p role="alert" className="mt-2 text-sm text-danger">{error}</p>}

        <div className="mt-4 flex gap-2">
          <input
            aria-label={t("speaking.typeFallback")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSendTyped();
            }}
            className="w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
            placeholder={t("speaking.typeFallback")}
          />
          <Button variant="secondary" onClick={onSendTyped} disabled={busy || !input.trim()}>
            {t("speaking.send")}
          </Button>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <Badge tone="brand">{t("speaking.aiPartner")}</Badge>
          <Button variant="danger" onClick={() => void chat.end()} loading={busy}>
            {t("bot.end")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
